import React from "react";
import PropTypes from "prop-types";
import Input from "../../common/Input";
import SelectInput from "../../common/SelectInput";
import SearchableSelect from "../../common/SearchableSelect";
import { STATUS } from "../../common/Const";
import { PARTICIPATION_STRATEGIES, PAYMENT_STRATEGIES } from "../../billingEnrollment/billingEnrollmentTypes";
import PaymentSchedulePreview from "./PaymentSchedulePreview";
import { getProductDetails } from "./enrollmentDisplay";

const labelForPerson = item => `${item.firstName || ""} ${item.lastName || ""}`.trim() || item.id;
const optionsForPeople = items => items.map(item => ({ value: item.id, text: labelForPerson(item) }));
const optionsForProducts = items => items.map(item => ({ value: item.id, text: item.name || item.id }));
const getProductById = (products, id) => products.find(product => product.id === id) || null;
const LOCKED_PAYMENT_SCHEDULE_STATUSES = ["active", "expired", "depleted", "cancelled"];

function EnrollmentForm({
  clients,
  students,
  products,
  enrollment,
  errors,
  onChange,
  onScheduleItemChange,
  onAddScheduleItem,
  onRemoveScheduleItem,
  onSubmit,
  onCancel,
  status,
}) {
  const disabled = status === STATUS.SUBMITTING;
  const selectedProduct = getProductById(products, enrollment.productId) || enrollment.product || null;
  const usesSharedStudents = selectedProduct
    && selectedProduct.participationStrategy === PARTICIPATION_STRATEGIES.SHARED_STUDENTS;
  const studentsForSelectedClient = students.filter(student => student.clientId === enrollment.clientId);
  const productDetails = selectedProduct ? getProductDetails(selectedProduct, enrollment.status) : null;
  const usesPaymentSchedule = selectedProduct && selectedProduct.paymentStrategy === PAYMENT_STRATEGIES.INSTALLMENTS;
  const isPaymentScheduleLocked = LOCKED_PAYMENT_SCHEDULE_STATUSES.includes(enrollment.status);

  return (
    <form id="enrollment-form" onSubmit={onSubmit}>
      <h2 className="manage-title">Zapis</h2>
      <div className="form-row">
        <div className="col-md-4">
          <SearchableSelect
            label="Klient"
            name="clientId"
            value={enrollment.clientId || ""}
            placeholder="Wybierz klienta"
            options={optionsForPeople(clients)}
            error={errors.clientId}
            onChange={onChange}
          />
        </div>
        <div className="col-md-4">
          <SelectInput
            label="Produkt"
            name="productId"
            value={enrollment.productId || ""}
            defaultOption="Wybierz produkt"
            options={optionsForProducts(products)}
            error={errors.productId}
            onChange={onChange}
          />
        </div>
        <div className="col-md-4">
          {usesSharedStudents ? (
            <div className="form-group">
              <span><strong>Uprawnieni studenci</strong></span>
              <div className="field mt-1">
                <select
                  name="eligibleStudentIds"
                  multiple
                  value={enrollment.eligibleStudentIds || enrollment.studentIds || []}
                  onChange={onChange}
                  className={`form-control ${errors.eligibleStudentIds ? "is-invalid" : ""}`}
                  aria-label="Uprawnieni studenci"
                >
                  {optionsForPeople(studentsForSelectedClient).map(option => (
                    <option key={option.value} value={option.value}>
                      {option.text}
                    </option>
                  ))}
                </select>
                {errors.eligibleStudentIds && <div className="invalid-feedback">{errors.eligibleStudentIds}</div>}
              </div>
            </div>
          ) : (
            <SelectInput
              label="Student"
              name="studentIds"
              value={(enrollment.studentIds || enrollment.eligibleStudentIds || [])[0] || ""}
              defaultOption="Wybierz studenta"
              options={optionsForPeople(studentsForSelectedClient)}
              error={errors.studentIds || errors.eligibleStudentIds}
              onChange={onChange}
            />
          )}
        </div>
      </div>
      {productDetails && (
        <section className="mt-2 mb-2">
          <h3 className="manage-title h5">Parametry produktu</h3>
          <div className="form-row">
            <div className="col-md-3">
              <Input label="Status zapisu" name="enrollmentStatusLabel" value={productDetails.status} readOnly />
            </div>
            <div className="col-md-3">
              <Input label="Cena" name="productDefaultPrice" value={productDetails.defaultPrice} readOnly />
            </div>
            <div className="col-md-3">
              <Input label="Platnosc" name="productPaymentStrategy" value={productDetails.paymentStrategy} readOnly />
            </div>
            <div className="col-md-3">
              <Input label="Naliczanie" name="productChargingStrategy" value={productDetails.chargingStrategy} readOnly />
            </div>
          </div>
          <div className="form-row">
            <div className="col-md-3">
              <Input label="Uczestnictwo" name="productParticipationStrategy" value={productDetails.participationStrategy} readOnly />
            </div>
            <div className="col-md-3">
              <Input label="Liczba lekcji" name="productPackageUnits" value={productDetails.packageUnits} readOnly />
            </div>
            <div className="col-md-3">
              <Input label="Typ lekcji" name="productClassType" value={productDetails.classType} readOnly />
            </div>
            <div className="col-md-3">
              <Input label="Waznosc" name="productValidity" value={productDetails.validity} readOnly />
            </div>
          </div>
        </section>
      )}
      <div className="form-row">
        <div className="col-md-3">
          <Input
            label="Start"
            name="startDate"
            type="date"
            value={enrollment.startDate || ""}
            error={errors.startDate}
            onChange={onChange}
          />
        </div>
      </div>
      {usesPaymentSchedule && (
      <section className="mt-2 mb-2">
        <h3 className="manage-title h5">Plan platnosci</h3>
        <p className="manage-text mb-2">
          Ten plan pokazuje naleznosci wynikajace z zapisu. Wplaty klienta rejestruj w rozliczeniach klienta.
        </p>
      {!isPaymentScheduleLocked && (
        <button
          type="button"
          className="btn manage-button manage-teal-button mt-2"
          disabled={disabled}
          onClick={onAddScheduleItem}
        >
          Dodaj planowana platnosc
        </button>
      )}
      <PaymentSchedulePreview
        items={enrollment.paymentScheduleItems || []}
        onChange={onScheduleItemChange}
        onRemove={onRemoveScheduleItem}
        readOnly={isPaymentScheduleLocked}
      />
      </section>
      )}
      <div className="pt-4 row mx-0">
        <div className="ml-auto col-md-block">
          <button type="submit" className="btn manage-button manage-red-button btn-block" disabled={disabled}>
            {disabled ? "Zapisywanie..." : "Zapisz"}
          </button>
        </div>
        <div className="col-md-block pl-2 pr-2">
          <button type="button" className="btn manage-button manage-teal-button btn-block" disabled={disabled} onClick={onCancel}>
            Powrot
          </button>
        </div>
      </div>
    </form>
  );
}

EnrollmentForm.propTypes = {
  clients: PropTypes.array.isRequired,
  students: PropTypes.array.isRequired,
  products: PropTypes.array.isRequired,
  enrollment: PropTypes.object.isRequired,
  errors: PropTypes.objectOf(PropTypes.string),
  onChange: PropTypes.func.isRequired,
  onScheduleItemChange: PropTypes.func,
  onAddScheduleItem: PropTypes.func,
  onRemoveScheduleItem: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  status: PropTypes.string,
};

EnrollmentForm.defaultProps = {
  errors: {},
  onScheduleItemChange: () => {},
  onAddScheduleItem: () => {},
  onRemoveScheduleItem: () => {},
  onCancel: () => {},
  status: STATUS.IDLE,
};

export default EnrollmentForm;
